import { EFFORT_HARD_SET_RIR, effectiveRir } from "./effortScale.js";
import type { LogSet, NewLogSet } from "./schemas/logSet.js";
import {
  isCountedLogSet,
  NOMINAL_COUNTED_BODYWEIGHT_KG,
  type SetLoadLookup,
} from "./setMetrics.js";

/** #536 / #51: the one test for "this session was real work", on its LogSets.
 *
 *  Option A (the main loop's rule, flipped by changing this predicate): a
 *  session with no COUNTED set (isCountedLogSet, the predicate behind
 *  ExerciseResult.setCount) is not a workout; and neither is one whose counted
 *  sets all carry a recorded effort and none is hard (RIR <= EFFORT_HARD_SET_RIR):
 *  the abandoned air-squat x5 @ RIR 10.
 *
 *  ⛔ Not "has a hard set" alone: imported StrengthLog history has no RIR, so
 *  its sets are effort-UNKNOWN (effectiveRir null) and the session stays work
 *  (#419; same reading as countsAsStrength). A set the athlete did not tag is
 *  never judged easy. Cardio counts no set (setMetrics), so it is not work
 *  either, as for countsAsStrength.
 *
 *  `loadOf` and `bodyweightKg` feed isCountedLogSet, as in loggedHardSetsBySlug:
 *  a caller without the athlete's bodyweight leaves the nominal one. */
export const isWorkSession = (
  sets: ReadonlyArray<LogSet | NewLogSet>,
  loadOf: (slug: string) => SetLoadLookup | undefined = () => undefined,
  bodyweightKg: number = NOMINAL_COUNTED_BODYWEIGHT_KG,
): boolean => {
  let counted = 0;
  let anyUntagged = false;
  for (const s of sets) {
    if (!isCountedLogSet(s as LogSet, loadOf(s.exerciseSlug), bodyweightKg)) continue;
    counted += 1;
    const rir = effectiveRir(s);
    if (rir === null) anyUntagged = true;
    else if (rir <= EFFORT_HARD_SET_RIR) return true;
  }
  return counted > 0 && anyUntagged;
};
